/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { LogDetails } from "../../types";
import type {
  TAggregated,
  TConfiguration,
  TEntitlement,
  TPcd,
  TPgd,
  TPgdDetails,
  TPlaybackSessionSnapshot,
  TSessionState,
  TSnapshot,
  TStatus
} from "./types";
import {
  formatEpochSeconds3dp,
  formatMbps2dp,
  formatPercent1dp,
  formatRoundedInteger,
  putBoolean,
  putNumber,
  putString
} from "./utils";

/**
 * Converts the grouped `TPlaybackSessionSnapshot` into the flat short-name
 * wire map expected by the analytics transport, applying the omit-empty
 * and formatting rules from spec §5. Also builds the `playbackStart`/
 * `playbackEnd` operational payloads (see `playback-start-end-operational-
 * events.md`), which reuse the same per-category serialization plus the
 * operational-only `snapshot` group.
 */
export class PlaybackSessionSnapshotSerializer {
  public static toShortNameMap(snapshot: TPlaybackSessionSnapshot): LogDetails {
    const target: LogDetails = {};
    const { pgd, pgdDetails, pcd, state, entitlement, aggregated, status } =
      snapshot;

    PlaybackSessionSnapshotSerializer.putPgd(target, pgd);
    PlaybackSessionSnapshotSerializer.putPgdDetails(target, pgdDetails);
    PlaybackSessionSnapshotSerializer.putPcd(target, pcd);
    PlaybackSessionSnapshotSerializer.putState(target, state);
    PlaybackSessionSnapshotSerializer.putEntitlement(target, entitlement);
    PlaybackSessionSnapshotSerializer.putAggregated(target, aggregated);
    PlaybackSessionSnapshotSerializer.putStatus(target, status);

    return target;
  }

  /**
   * Builds the `playbackStart` operational payload — `configuration`/`pgd`/
   * `pgd_details`/`state`/`pcd`/`status` (`pcd` is always empty today, same
   * as `playbackEnd`; `status` carries only `fc`/`vt` since no outcome
   * exists yet). `snapshot` (point-in-time player state) is only
   * meaningful once playback is underway, so it's sent on `playbackEnd`
   * only. `configuration` is operational-only — never sent on
   * `playbackSession` (spec §4/§8).
   */
  public static toOperationalStartMap(
    pgd: TPgd,
    pgdDetails: TPgdDetails,
    configuration: TConfiguration,
    state: TSessionState,
    pcd: TPcd,
    status: TStatus
  ): LogDetails {
    const target: LogDetails = {};
    PlaybackSessionSnapshotSerializer.putPgd(target, pgd);
    PlaybackSessionSnapshotSerializer.putPgdDetails(target, pgdDetails);
    PlaybackSessionSnapshotSerializer.putConfiguration(target, configuration);
    PlaybackSessionSnapshotSerializer.putState(target, state);
    PlaybackSessionSnapshotSerializer.putPcd(target, pcd);
    PlaybackSessionSnapshotSerializer.putStatus(target, status);
    return target;
  }

  /**
   * Builds the `playbackEnd` operational payload by reusing the full
   * analytics wire map and adding `configuration` and `snapshot`. `status`
   * (including `em`/error_message) is included wholesale, matching the
   * spec — it is populated precisely when the session ended in failure.
   */
  public static toOperationalEndMap(
    sessionSnapshot: TPlaybackSessionSnapshot,
    configuration: TConfiguration,
    playerSnapshot: TSnapshot
  ): LogDetails {
    const target =
      PlaybackSessionSnapshotSerializer.toShortNameMap(sessionSnapshot);
    PlaybackSessionSnapshotSerializer.putConfiguration(target, configuration);
    PlaybackSessionSnapshotSerializer.putSnapshot(target, playerSnapshot);
    return target;
  }

  /**
   * Builds the `playbackHealth` operational payload — `configuration`/
   * `pgd`/`pgd_details`/`state`/`pcd`/`aggregated` (live cumulative
   * counters, still growing) plus `snapshot` and the tick timestamp as
   * `et`. No other `status` field is set — the run has not ended (spec §5.4).
   */
  public static toOperationalHealthMap(
    pgd: TPgd,
    pgdDetails: TPgdDetails,
    configuration: TConfiguration,
    state: TSessionState,
    pcd: TPcd,
    aggregated: TAggregated,
    playerSnapshot: TSnapshot,
    tickTimeEpochSeconds: number
  ): LogDetails {
    const target: LogDetails = {};
    PlaybackSessionSnapshotSerializer.putPgd(target, pgd);
    PlaybackSessionSnapshotSerializer.putPgdDetails(target, pgdDetails);
    PlaybackSessionSnapshotSerializer.putConfiguration(target, configuration);
    PlaybackSessionSnapshotSerializer.putState(target, state);
    PlaybackSessionSnapshotSerializer.putPcd(target, pcd);
    PlaybackSessionSnapshotSerializer.putAggregated(target, aggregated);
    PlaybackSessionSnapshotSerializer.putSnapshot(target, playerSnapshot);
    putNumber(target, "et", tickTimeEpochSeconds, formatEpochSeconds3dp);
    return target;
  }

  private static putConfiguration(
    target: LogDetails,
    configuration: TConfiguration
  ): void {
    putString(target, "sty", configuration.sessionType);
    putString(target, "stt", configuration.streamType);
    putString(target, "vsrc", configuration.videoSource);
    putString(target, "abd", configuration.assetBounds);
    putString(target, "vp", configuration.videoPlayer);
    putBoolean(target, "is_ppv", configuration.isPayPerView);
    putString(target, "connection_mode", configuration.connectionMode);
    putString(target, "streaming_protocol", configuration.streamingProtocol);
    putString(target, "region_mode", configuration.regionMode);
    putString(
      target,
      "max_channel_dimensions",
      configuration.maxChannelDimensions
    );
    putString(target, "drm_type", configuration.drmType);
    putString(target, "video_codec", configuration.videoCodec);
    putString(target, "audio_codec", configuration.audioCodec);
    putString(target, "container_format", configuration.containerFormat);
    putString(target, "resolution", configuration.resolution);
    putString(target, "aspect_ratio", configuration.aspectRatio);
  }

  private static putPgd(target: LogDetails, pgd: TPgd): void {
    putString(target, "clid", pgd.collectionId);
    putString(target, "clt", pgd.collectionTitle);
    putString(target, "cid", pgd.contentId);
    putString(target, "ct", pgd.contentTitle);
    putString(target, "provider_id", pgd.providerId);
    putString(target, "stid", pgd.stationId);
    putString(target, "chid", pgd.channelId);
    putString(target, "ccs", pgd.channelCallSign);
    putString(target, "chn", pgd.channelNumber);
  }

  private static putPgdDetails(
    target: LogDetails,
    pgdDetails: TPgdDetails
  ): void {
    putNumber(target, "pst", pgdDetails.programStartTime);
    putNumber(target, "cd", pgdDetails.contentDuration);
    putString(target, "cty", pgdDetails.contentType);
    putString(target, "cn", pgdDetails.contentNetwork);
    putNumber(target, "sn", pgdDetails.seasonNumber);
    putNumber(target, "epn", pgdDetails.episodeNumber);
    putString(target, "oad", pgdDetails.originalAirDate);
    putNumber(target, "yr", pgdDetails.year);
    putString(target, "gn", pgdDetails.genre);
  }

  private static putPcd(target: LogDetails, pcd: TPcd): void {
    putString(target, "qid", pcd.queryId);
    putString(target, "iid", pcd.itemId);
  }

  private static putState(target: LogDetails, state: TSessionState): void {
    putString(target, "pbId", state.playbackId);
    putNumber(target, "st", state.startTime, formatEpochSeconds3dp);
    putString(target, "url", state.videoPlayerUrl);
    putString(target, "media_id", state.mediaId);
    putBoolean(target, "cc_enabled", state.closedCaptionEnabled);
  }

  private static putEntitlement(
    target: LogDetails,
    entitlement: TEntitlement
  ): void {
    putString(target, "vat", entitlement.vodAssetType);
    putString(target, "loc", entitlement.locality);
  }

  private static putAggregated(
    target: LogDetails,
    aggregated: TAggregated
  ): void {
    putNumber(target, "vst", aggregated.videoStartTime, formatRoundedInteger);
    putNumber(
      target,
      "vpst",
      aggregated.videoPlayingStartTime,
      formatRoundedInteger
    );
    putNumber(target, "avbr", aggregated.avgVideoBitrateMbps, formatMbps2dp);
    putNumber(target, "anbr", aggregated.avgNetworkBitrateMbps, formatMbps2dp);
    putNumber(target, "vbr", aggregated.videoBitrateMbps, formatMbps2dp);
    putNumber(target, "nbr", aggregated.networkBitrateMbps, formatMbps2dp);
    putNumber(
      target,
      "megabytes_received",
      aggregated.megabytesReceived,
      formatRoundedInteger
    );
    putNumber(target, "vfp", aggregated.videoFramesPresented);
    putNumber(target, "vfd", aggregated.videoFramesDropped);
    putNumber(target, "psc", aggregated.profileShiftCount);
    putNumber(target, "uec", aggregated.userErrorCount);
    putString(target, "utc", aggregated.userTransientCodes);
    putNumber(
      target,
      "vbt",
      aggregated.videoBufferingTime,
      formatRoundedInteger
    );
    putNumber(target, "vbc", aggregated.videoBufferingCount);
    putNumber(
      target,
      "vslt",
      aggregated.videoStallingTime,
      formatRoundedInteger
    );
    putNumber(target, "vslc", aggregated.videoStallingCount);
    putNumber(target, "vpt", aggregated.videoPlayingTime, formatRoundedInteger);
    putNumber(target, "vpc", aggregated.videoPlayingCount);
    putNumber(
      target,
      "vpat",
      aggregated.videoPausingTime,
      formatRoundedInteger
    );
    putNumber(target, "vpac", aggregated.videoPausingCount);
    putNumber(
      target,
      "vtpt",
      aggregated.videoTrickplayingTime,
      formatRoundedInteger
    );
    putNumber(target, "vtpc", aggregated.videoTrickplayingCount);
    putNumber(target, "so", aggregated.startOffsetSeconds);
    putNumber(target, "eo", aggregated.endOffsetSeconds);
  }

  private static putStatus(target: LogDetails, status: TStatus): void {
    putString(target, "fc", status.failureClassification);
    putString(target, "vt", status.validTransaction);
    putNumber(target, "dur", status.duration, formatRoundedInteger);
    putNumber(target, "et", status.endTime, formatEpochSeconds3dp);
    putString(target, "er", status.endReasonCode);
    putString(target, "rsn", status.reason);
    putString(target, "utrc", status.userTerminalCodes);
    putString(target, "ufnc", status.userFinalCodes);
    putString(target, "ec", status.errorCode);
    putString(target, "ety", status.errorType);
    putString(target, "em", status.errorMessage);
    putString(target, "sts", status.statusDisposition);
  }

  private static putSnapshot(target: LogDetails, snapshot: TSnapshot): void {
    putNumber(target, "pp", snapshot.playPositionMs, formatRoundedInteger);
    putString(target, "ss", snapshot.streamingState);
    putNumber(
      target,
      "vba",
      snapshot.videoBufferAvailablePercent,
      formatPercent1dp
    );
    putString(target, "cc_language", snapshot.ccLanguage);
  }
}
